import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import BooksPage from './pages/BooksPage';
import CustomersPage from './pages/CustomersPage';
import ReportsPage from './pages/ReportsPage';
import UsersManagementPage from './pages/UsersManagementPage';
import SettingsPage from './pages/SettingsPage';
import BackupManagementPage from './pages/BackupManagementPage';
import PendingCustomersPage from './pages/PendingCustomersPage';
import LoginPage from './pages/LoginPage';
import PrivateRoute from './components/PrivateRoute';
import { getAuthToken, setAuthToken } from './services/api';
import './App.css';

function AppContent() {
  const [activeTab, setActiveTab] = useState('books');
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    // עדכן את activeTab לפי הנתיב הנוכחי
    if (location.pathname === '/customers') {
      setActiveTab('customers');
    } else if (location.pathname === '/reports') {
      setActiveTab('reports');
    } else if (location.pathname === '/pending-customers') {
      setActiveTab('pending-customers');
    } else if (location.pathname === '/books' || location.pathname === '/') {
      setActiveTab('books');
    }
  }, [location.pathname]);

  useEffect(() => {
    // טעין מידע משתמש מ-localStorage כשה-token קיים
    const token = getAuthToken();
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        console.error('Error parsing user data:', e);
        setUser(null);
      }
    } else {
      // אם אין token, ודא שאין user
      setUser(null);
    }

    // הקשב לשינויים ב-localStorage (כשמתחברים או מתנתקים בטאב אחר)
    const handleStorageChange = () => {
      const newToken = getAuthToken();
      const newUserData = localStorage.getItem('user');
      
      if (newToken && newUserData) {
        try {
          setUser(JSON.parse(newUserData));
        } catch (e) {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    };

    // הקשב לאירוע התחברות מיידי (same-tab)
    const handleUserLogin = () => {
      const newToken = getAuthToken();
      const newUserData = localStorage.getItem('user');
      
      if (newToken && newUserData) {
        try {
          setUser(JSON.parse(newUserData));
        } catch (e) {
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-login', handleUserLogin);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-login', handleUserLogin);
    };
  }, []);

  const handleLogout = () => {
    setAuthToken(null); // זה מנקה את headers ו-localStorage
    setUser(null);
    navigate('/login');
  };

  // אם המשתמש לא מחובר, עבור לעמוד התחברות
  if (!getAuthToken() && location.pathname !== '/login') {
    return <LoginPage />;
  }

  // אם נמצא בעמוד התחברות, הצג רק את העמוד
  if (location.pathname === '/login') {
    return <Routes>
      <Route path="/login" element={<LoginPage />} />
    </Routes>;
  }

  return (
    <div className="App">
      <header className="app-header">
        <h1>📚 מערכת ניהול ספריה</h1>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <nav className="nav-tabs">
            <Link 
              to="/books" 
              className={`nav-link ${activeTab === 'books' ? 'active' : ''}`}
              onClick={() => setActiveTab('books')}
            >
              📖 ספרים
            </Link>
            <Link 
              to="/customers" 
              className={`nav-link ${activeTab === 'customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('customers')}
            >
              👥 לקוחות
            </Link>
            <Link 
              to="/reports" 
              className={`nav-link ${activeTab === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveTab('reports')}
            >
              📊 דוחות
            </Link>
            <Link 
              to="/pending-customers" 
              className={`nav-link ${activeTab === 'pending-customers' ? 'active' : ''}`}
              onClick={() => setActiveTab('pending-customers')}
            >
              ⏳ לקוחות ממתינים
            </Link>
            {user && user.role === 'admin' && (
              <Link 
                to="/users" 
                className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                👤 משתמשים
              </Link>
            )}
            {user && user.role === 'admin' && (
              <Link 
                to="/settings" 
                className={`nav-link ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                ⚙️ הגדרות
              </Link>
            )}
            {user && user.role === 'admin' && (
              <Link 
                to="/backups" 
                className={`nav-link ${activeTab === 'backups' ? 'active' : ''}`}
                onClick={() => setActiveTab('backups')}
              >
                💾 גיבויים
              </Link>
            )}
          </nav>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {user && (
              <span style={{ color: '#666', fontSize: '14px' }}>
                {user.username} {user.role === 'admin' && '(מנהל)'}
              </span>
            )}
            {user && (
              <button 
                onClick={handleLogout}
                style={{ 
                  padding: '8px 15px', 
                  backgroundColor: '#dc3545', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: '4px', 
                  cursor: 'pointer' 
                }}
              >
                התנתקות
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="main-content">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/books" element={<PrivateRoute><BooksPage /></PrivateRoute>} />
          <Route path="/customers" element={<PrivateRoute><CustomersPage /></PrivateRoute>} />
          <Route path="/reports" element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
          <Route path="/pending-customers" element={<PrivateRoute><PendingCustomersPage /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><UsersManagementPage /></PrivateRoute>} />
          <Route path="/settings" element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
          <Route path="/backups" element={<PrivateRoute><BackupManagementPage /></PrivateRoute>} />
          <Route path="/" element={<PrivateRoute><BooksPage /></PrivateRoute>} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
